import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES as API_ROUTES } from "../../constants/api";
import { GetApiData } from "../../api/general";
import { SetSendMoneyLimitsContent } from "../../actions/sendMoney";

// types
import type { RootState } from "../../types/redux";
import type { GetSendMoneyLimitsResponse } from "../../types/api/sendMoney";
import type { SendMoneyPage } from "../../types/sendMoney";
import type { DisplaySectionType } from "../../types/components";
import type { DisclaimerType } from "../../types/api/moneyTransfer";
import { mockSendMoneyHeaders } from "../../constants/mockTestData";

// stable empties so consumers don't see a new array every render
const NO_SECTIONS: DisplaySectionType[] = [];
const NO_DISCLAIMERS: DisclaimerType[] = [];

// Re-key the payload: every field comes back id: 0 and sections have no id,
// so map both axes to unique ids before feeding DisplaySection.
const toSections = (page: SendMoneyPage): DisplaySectionType[] =>
  page.displaySections.map((section, s) => ({
    name: `send-money-limits-${s}`,
    id: `send-money-limits-${s}`,
    label: "",
    fields: section.fields.map((field, f) => ({ ...field, id: s * 100 + f })),
  }));

// A quote is more specific than a country, so it wins when both are present.
const resolveLimitsUrl = (quoteId: number | null, countryIso: string): string | null => {
  if (quoteId != null) return API_ROUTES.SEND_MONEY.GET_SEND_MONEY_LIMITS_BY_QUOTE(quoteId);
  if (countryIso) return API_ROUTES.SEND_MONEY.GET_SEND_MONEY_LIMITS_BY_COUNTRY(countryIso);
  return null;
};

/**
 * Fetches the limits content (by quote, else by country) and caches it in Redux
 * so the limits modal and the wallet modal's inline expand share one fetch.
 * Failures are reported via hasError + error; each consumer decides how to show them.
 */
const useSendMoneyLimits = () => {
  const dispatch = useDispatch();
  const quoteId = useSelector((state: RootState) => state.sendMoney.quoteId);
  const countryIso = useSelector((state: RootState) => state.sendMoney.selectedCountry);
  const cached = useSelector((state: RootState) => state.sendMoney.limitsContent);

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const url = resolveLimitsUrl(quoteId, countryIso);

  // the cache only counts when it was fetched for the current quote/country
  const current = url !== null && cached?.key === url ? cached : null;

  const fetchLimits = useCallback(async () => {
    if (!url || current) return;

    setLoading(true);
    setHasError(false);
    setError(null);

    try {
      const response = await GetApiData<GetSendMoneyLimitsResponse>(url);

      // Transform inside the try so a malformed payload lands in the error
      // state instead of throwing during render. The cache write doesn't need
      // the component, so it happens even if the view closed mid-request.
      const { page, footer } = response.result;
      dispatch(
        SetSendMoneyLimitsContent({
          key: url,
          sections: toSections(page),
          disclaimers: footer?.disclaimers ?? [],
          title: page.title ?? "",
          subTitle: page.subTitle ?? "",
        })
      );
    } catch (err) {
      if (!mounted.current) return;
      setHasError(true);
      setError(err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [url, current, dispatch]);

  return {
    loading,
    sections: current?.sections ?? NO_SECTIONS,
    disclaimers: current?.disclaimers ?? NO_DISCLAIMERS,
    title: current?.title ?? "",
    subTitle: current?.subTitle ?? "",
    hasError,
    error,
    fetchLimits,
  };
};

export default useSendMoneyLimits;